import * as React from 'react';
import {createSelector} from 'reselect';

import {
  matchExpression,
  Expressions,
  TypeImplementation,
} from '@packages/systems/wfdl';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  TText,
  TList,
  EText,
  EList,
  EHole,
  Expression,
} from '@packages/systems/wfdl-ast';

import {PropertyListNone} from '@packages/systems/ui/shared-ui/PropertyList';
import {Users12} from '@packages/systems/ui/icons';
import {
  SearchInputPopoverList,
  RenderMatch,
  RenderItemLabelHelper,
} from '@packages/systems/flows/shared';
// eslint-disable-next-line webflow/module-top-level-imports
import {ItemWithId} from '@packages/systems/flows/shared/components/SearchInputPopoverList';

import {stripAll} from '@packages/utilities/contextual-expression';
import {
  DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';

const ALL_COLLABORATORS = 'all_collaborators';

// @ts-expect-error - TS7006 - Parameter 'userRecord' implicitly has an 'any' type.
function getUserLabel(userRecord) {
  return `${userRecord.firstName || ''} ${userRecord.lastName || ''}`.trim();
}

// @ts-expect-error - TS7006 - Parameter 'userRecord' implicitly has an 'any' type.
function getUserRole(userRecord, siteOwnerId: any) {
  if (userRecord.id === siteOwnerId) {
    return 'Owner';
  }
  return 'Staff';
}

const selectSiteUsers = createSelector(
  (state: DesignerStores) => state.UserStore.siteUsers,
  // filter unverified users which are the ones that don't have firstName and lastName defined
  // filter users that has .user defined since this means the user it's already present in the list
  (siteUsers) =>
    siteUsers
      // @ts-expect-error - TS7006 - Parameter 'u' implicitly has an 'any' type.
      .filter((u) => u.firstName && u.lastName && !u.user)
      .map((user) => ({...user.toObject(), id: user._id}))
);
const selectSiteOwnerId = (state: any) => state.SiteDataStore.userId;

const NoPropertyItem = () => (
  <PropertyListNone label="No collaborators added" />
);

const filterIsNotUserId = (userId: string) =>
  matchExpression({
    Text: (id) => id !== userId,
    _: () => true,
  });

const allOptionConfig = {
  id: ALL_COLLABORATORS,
  label: 'All collaborators',
  icon: <Users12 />,
} as const;

export const CollaboratorsList = TypeImplementation.Alias({
  type: TList(TText),
  system: {bindUIVariant: 'custom'},
  render: function RenderEmailCollaborators({expr: contextualExpr, dispatch}) {
    // TODO: this is not ideal https://webflow.atlassian.net/browse/LOGIC-3738
    const expr = stripAll(contextualExpr);
    const siteOwnerId = useDesignerSelector(selectSiteOwnerId);
    const collaborators = useDesignerSelector(selectSiteUsers);

    const list = React.useMemo(() => Expressions.getList(expr) || [], [expr]);

    const searchList = React.useMemo(() => {
      const result: Array<ItemWithId<never>> = [];

      const listSet = new Set(list.map(Expressions.getText));

      for (const [userId, userRecord] of collaborators) {
        if (!listSet.has(userId) && userRecord) {
          // @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'.
          result.push(userRecord);
        }
      }
      return result;
    }, [list, collaborators]);

    const onCollaboratorSelect = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'userRecord' implicitly has an 'any' type.
      (userRecord) => {
        if (userRecord.id === ALL_COLLABORATORS) {
          const collaboratorIds: Array<Expression> = [];

          // Adds all collaborators with emails to the list
          for (const [, collaborator] of collaborators) {
            if (collaborator.emailHash && collaborator.id) {
              collaboratorIds.push(EText(collaborator.id));
            }
          }
          dispatch(updateExpressionAction(EList(collaboratorIds)));
        } else {
          dispatch(
            updateExpressionAction(EList(list.concat(EText(userRecord.id))))
          );
        }
      },
      [dispatch, list, collaborators]
    );

    const onCollaboratorRemove = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'userId' implicitly has an 'any' type.
      (userId) => {
        const updatedList = list.filter(filterIsNotUserId(userId));
        const payload = updatedList.length > 0 ? EList(updatedList) : EHole();

        dispatch(updateExpressionAction(payload));
      },
      [dispatch, list]
    );

    const items = React.useMemo(() => {
      const result: Array<ItemWithId<never>> = [];

      for (const textExpr of list) {
        const id = Expressions.getText(textExpr);
        if (id == null) continue;
        const userRecord = collaborators.get(id);
        // @ts-expect-error - TS2345 - Argument of type '{ id: string; label: string; role: string; }' is not assignable to parameter of type 'never'.
        result.push({
          id,
          label: getUserLabel(userRecord),
          role: getUserRole(userRecord, siteOwnerId),
        });
      }

      return result;
    }, [list, collaborators, siteOwnerId]);

    return (
      <SearchInputPopoverList
        searchList={searchList}
        allOptionConfig={allOptionConfig}
        onItemSelect={onCollaboratorSelect}
        getSearchableText={getUserLabel}
        itemTypeLabel="collaborators"
        visuallyHiddenText={`Email notifications can only be sent to project collaborators.
          Manage collaborators and email settings from your project
          settings.`}
        renderMatch={({preselected, match}) => (
          <RenderMatch
            highlight={preselected === match}
            labels={[
              {
                label: `${getUserLabel(match)} `,
              },
              {
                isSecondary: true,
                label: `(${getUserRole(match, siteOwnerId)})`,
              },
            ]}
          />
        )}
        selectedItemsConfig={{
          name: 'collaborator-list',
          onItemRemoved: onCollaboratorRemove,
          renderNone: NoPropertyItem,
          items,
          renderPropertyListItemLabel: ({label, role}) => (
            <RenderItemLabelHelper primaryLabel={label} secondaryLabel={role} />
          ),
        }}
      />
    );
  },
});
